import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { auth } from "@/src/config/firebase";
import {
  useGetMyReferralCode,
  useListMyReferrals,
} from "@dataconnect/generated/react";
import { buildReferralShareUrl } from "@/src/services/referralService";
import {
  trackReferralLinkCopied,
  trackReferralLinkGenerated,
  trackReviewPortalOpened,
} from "@/src/services/analyticsService";

const REFERRAL_REWARD_CREDITS = 100;

const REVIEW_TASKS = [
  {
    id: "omr",
    platform: "OMR",
    url: "https://omr.com/en/reviews/new/product/getshowcase-ai",
    titleKey: "referralHub.tasks.omr.title",
    descriptionKey: "referralHub.tasks.omr.description",
  },
  {
    id: "capterra",
    platform: "Capterra",
    url: "https://reviews.capterra.com/products/new/042d58e3-7220-4863-9d4e-640c3616b636/",
    titleKey: "referralHub.tasks.capterra.title",
    descriptionKey: "referralHub.tasks.capterra.description",
  },
  {
    id: "g2",
    platform: "G2",
    url: "https://www.g2.com/products/getshowcase-ai/reviews",
    titleKey: "referralHub.tasks.g2.title",
    descriptionKey: "referralHub.tasks.g2.description",
  },
] as const;

interface ReferralHubState {
  /** Recorded referral count (all statuses). Drives the "X signups recorded" hint. */
  referralCount: number;
  /** The current user's referral slug; null while loading or unset. */
  referralCode: string | null;
}

function useReferralHubState(): ReferralHubState {
  const { data: codeData } = useGetMyReferralCode({
    enabled: !!auth.currentUser,
  });
  const { data: referralsData } = useListMyReferrals({
    enabled: !!auth.currentUser,
  });

  return {
    referralCode: codeData?.user?.referralCode ?? null,
    referralCount: referralsData?.referrals.length ?? 0,
  };
}

const GiftIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 11.25v8.25A2.25 2.25 0 0 1 18.75 21H5.25A2.25 2.25 0 0 1 3 18.75v-7.5m18 0H3m18 0A2.25 2.25 0 0 0 18.75 9H5.25A2.25 2.25 0 0 0 3 11.25m9 9.75V9m0 0H7.875a2.625 2.625 0 1 1 2.625-2.625C10.5 7.824 12 9 12 9Zm0 0h4.125A2.625 2.625 0 1 0 13.5 6.375C13.5 7.824 12 9 12 9Z"
    />
  </svg>
);

const ExternalLinkIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 6H18m0 0v4.5M18 6 10.5 13.5m-3-6h-1.5A2.25 2.25 0 0 0 3.75 9.75v8.25A2.25 2.25 0 0 0 6 20.25h8.25A2.25 2.25 0 0 0 16.5 18v-1.5"
    />
  </svg>
);

const CopyIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
    />
  </svg>
);

function ReviewTaskItem({ task }: { task: (typeof REVIEW_TASKS)[number] }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {t(task.titleKey)}
          </h4>
          <p className="mt-0.5 text-xs text-gray-500">
            {t(task.descriptionKey, { count: REFERRAL_REWARD_CREDITS })}
          </p>
        </div>
        <a
          href={task.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackReviewPortalOpened(task.platform)}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          {t("referralHub.open")}
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}

function ReferralTaskItem({
  referralCode,
  referralCount,
}: {
  referralCode: string | null;
  referralCount: number;
}) {
  const { t } = useTranslation();
  const [justCopied, setJustCopied] = React.useState(false);

  const shareUrl = referralCode ? buildReferralShareUrl(referralCode) : null;

  // Fire `referral_link_generated` exactly once when the slug first becomes
  // available. Subsequent renders or remounts must not duplicate the event.
  const generatedRef = React.useRef(false);
  React.useEffect(() => {
    if (!referralCode || generatedRef.current) return;
    generatedRef.current = true;
    trackReferralLinkGenerated(referralCode);
  }, [referralCode]);

  const handleCopy = async () => {
    if (!shareUrl || !referralCode) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackReferralLinkCopied(referralCode);
      setJustCopied(true);
      toast.success(t("referralHub.copied"));
      window.setTimeout(() => setJustCopied(false), 2000);
    } catch {
      toast.error(t("referralHub.copyFailed"));
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="space-y-2.5">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            {t("referralHub.tasks.referral.title")}
          </h4>
          <p className="mt-0.5 text-xs text-gray-500">
            {t("referralHub.tasks.referral.description", {
              count: REFERRAL_REWARD_CREDITS,
            })}
          </p>
          {referralCount > 0 && (
            <p className="mt-1 text-xs font-medium text-green-700">
              {t("referralHub.tasks.referral.recorded", {
                count: referralCount,
              })}
            </p>
          )}
        </div>
        {shareUrl ? (
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={t("referralHub.tasks.referral.linkAria")}
              className="min-w-0 flex-1 truncate rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
              aria-label={t("referralHub.tasks.referral.copyAria")}
            >
              <CopyIcon className="h-3.5 w-3.5" />
              {justCopied
                ? t("referralHub.copied")
                : t("referralHub.tasks.referral.copyLink")}
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-500">
            {t("referralHub.tasks.referral.linkLoading")}
          </p>
        )}
      </div>
    </div>
  );
}

function ReferralHubPanel({
  compact = false,
  state,
}: {
  compact?: boolean;
  state: ReferralHubState;
}) {
  const { t } = useTranslation();
  const { referralCode, referralCount } = state;

  return (
    <div
      className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl shadow-black/5 ${
        compact ? "w-[min(92vw,420px)]" : ""
      }`}
    >
      <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm">
            <GiftIcon className="h-5 w-5 animate-gift-wiggle" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
              {t("referralHub.eyebrow")}
            </p>
            <h3 className="mt-1 text-base font-semibold text-gray-900">
              {t("referralHub.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-700">
              {t("referralHub.description", {
                count: REFERRAL_REWARD_CREDITS,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="space-y-2.5">
          {REVIEW_TASKS.map((task) => (
            <ReviewTaskItem key={task.id} task={task} />
          ))}
          <ReferralTaskItem
            referralCode={referralCode}
            referralCount={referralCount}
          />
        </div>

        <p className="mt-4 text-xs leading-relaxed text-gray-500">
          {t("referralHub.disclaimer")}
        </p>
      </div>
    </div>
  );
}

export function ReferralHubButton() {
  const { t } = useTranslation();
  const state = useReferralHubState();
  const [isOpen, setIsOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="group relative flex h-10 w-10 items-center justify-center rounded-full border border-amber-200/70 bg-amber-50/60 text-amber-900 shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-label={t("referralHub.openHub")}
      >
        <GiftIcon className="h-5 w-5 animate-gift-wiggle" />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-1 top-1 flex h-2 w-2"
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-3">
          <ReferralHubPanel compact state={state} />
        </div>
      )}
    </div>
  );
}

export function ReferralHubCard() {
  const { t } = useTranslation();
  const state = useReferralHubState();
  const [isOpen, setIsOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <section className="relative z-20 min-w-0 lg:col-span-2" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="group flex w-full items-center gap-4 rounded-xl border border-amber-200/70 bg-amber-50/60 px-4 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
        aria-expanded={isOpen}
      >
        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm">
          <GiftIcon className="h-5 w-5 animate-gift-wiggle" />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-900">
            {t("referralHub.settingsHeading")}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t("referralHub.description", { count: REFERRAL_REWARD_CREDITS })}
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors group-hover:bg-gray-50">
          {t(isOpen ? "referralHub.hidePerks" : "referralHub.showPerks")}
        </span>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-3">
          <ReferralHubPanel state={state} />
        </div>
      )}
    </section>
  );
}
